<template>
  <div class="gps-main">
    <Spinner class="is-size-3" v-if="isLoading" />

    <div class="notification is-danger" v-if="gpsLoadError">{{ gpsLoadError }}</div>

    <!-- Top card -->
    <div class="card" v-if="gps">
      <!-- Top card header -->
      <header class="card-header">
        <div class="card-header-title">
          <div class="media">
            <div class="media-content">
              <p>
                <span class="title is-4">{{ gps.deviceId }}_GPS</span>

                <button
                  v-if="canDelete"
                  class="button is-text is-size-6 has-text-danger"
                  @click="deleteDevice()"
                >
                  Delete
                </button>
              </p>
            </div>
          </div>
        </div>

        <a class="card-header-icon" @click="toggleTopCardContentClicked">
          <span class="icon is-size-4">
            <i v-if="topCardContentVisible" class="icon-chevron-down"></i>
            <i v-if="!topCardContentVisible" class="icon-chevron-right"></i>
          </span>
        </a>
      </header>

      <!-- Top card content -->
      <div v-if="topCardContentVisible" class="card-content">
        <div class="content is-flex">
          <!-- Left column -->
          <div class="table-container left-column">
            <table class="table is-narrow user-details">
              <tbody>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">IMEI</span>
                  </th>
                  <td>
                    <span v-if="gps.isWeHuntGPS">
                      <a @click="weHuntGPSIMEISelected(gps.imei)">{{ gps.imei }}</a>
                    </span>
                    <span v-else>{{ gps.imei }}</span>
                  </td>
                </tr>

                <tr v-if="gps.isWeHuntGPS">
                  <th class="is-narrow">
                    <span class="is-pulled-right">WeHunt GPS Owner</span>
                  </th>
                  <td>
                    <span v-if="gpsOwner">
                      {{ gpsOwner.firstName }} {{ gpsOwner.lastName }} (<span
                        >{{ gps.gpsOwnerUserId }}_LLUser</span
                      >)
                    </span>
                    <span v-else-if="gps.gpsOwnerUserId">{{ gps.gpsOwnerUserId }}_LLUser</span>
                    <span v-else>none</span>
                  </td>
                </tr>

                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Owner</span>
                  </th>
                  <td>
                    <span v-if="owner">
                      {{ owner.firstName }} {{ owner.lastName }} (<span
                        >{{ gps.ownerUserId }}_LLUser</span
                      >)
                    </span>
                    <span v-else-if="gps.ownerUserId === '[none]'">No WeHunt owner</span>
                    <span v-else>{{ gps.ownerUserId }}_LLUser</span>
                  </td>
                </tr>

                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Added At</span>
                  </th>
                  <td>
                    <span>{{ gps.createdAt }}</span>
                  </td>
                </tr>

                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Vendor</span>
                  </th>
                  <td>
                    <span>{{ gps.vendor }}</span>
                  </td>
                </tr>

                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Model</span>
                  </th>
                  <td>
                    <span>{{ gps.model }}</span>
                  </td>
                </tr>

                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Last Seen</span>
                  </th>
                  <td>
                    <span>{{ gps.statusUpdated || '[unknown]' }}</span>
                  </td>
                </tr>

                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Active Dog</span>
                  </th>
                  <td>
                    <span v-if="activeDogProfile">
                      {{ activeDogProfile.name }} (<span
                        >{{ gps.activeDogProfileId }}_DogProfile</span
                      >)
                    </span>
                    <span v-else-if="gps.activeDogProfileId">{{
                      gps.activeDogProfileId + '_DogProfile'
                    }}</span>
                    <span v-else>none</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Right column -->
          <div class="table-container">
            <table class="table is-narrow user-details">
              <tbody>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Covered by Subscription</span>
                  </th>
                  <td>
                    <Spinner
                      class="is-pulled-left spinner"
                      width="20"
                      height="20"
                      v-if="isLoadingSubscription"
                    />
                    <span class="has-text-danger" v-else-if="subscriptionLoadError">{{
                      subscriptionLoadError
                    }}</span>
                    <span v-else-if="subscription">
                      {{ subscription.type }}
                    </span>
                    <span v-else>
                      none
                    </span>

                    <span v-if="!isLoadingSubscription">
                      <a @click="recalculateSubscription()">Recalculate</a>
                    </span>
                  </td>
                </tr>

                <tr v-if="subscription">
                  <th class="is-narrow">
                    <span class="is-pulled-right">Expires</span>
                  </th>
                  <td>
                    <span>
                      {{ subscription.expiresAt }}
                    </span>
                  </td>
                </tr>

                <tr v-if="subscription">
                  <th class="is-narrow">
                    <span class="is-pulled-right">Auto Renews</span>
                  </th>
                  <td>
                    <span>
                      {{ subscription.autoRenews }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom tabs -->
    <div class="tabs is-toggle is-fullwidth bottom-tabs" v-if="gps">
      <ul>
        <li
          v-for="tab in bottomTabs"
          :key="tab.index"
          :class="{ 'is-active': tab.index === selectedBottomTabIndex }"
          @click="selectBottomTab(tab)"
        >
          <a>{{ tab.name }}</a>
        </li>
      </ul>
    </div>

    <!-- Bottom tab content -->
    <div class="tabs-content" v-if="gps">
      <component
        :is="selectedBottomTabComponent"
        :gpsDeviceStorePath="gpsDeviceStorePath"
        :usersCacheStorePath="usersCacheStorePath"
        :adminUserIds="adminUserIds"
        :activeDogProfile="activeDogProfile"
        @add-admin="addAdmin()"
        @remove-admin="removeAdmin($event)"
      ></component>
    </div>

    <!-- Modals -->
    <GPSDeviceRemoveAdminModal
      v-if="adminUserToRemove != null && gps"
      :gpsDeviceStorePath="gpsDeviceStorePath"
      :gps="gps"
      :adminUser="adminUserToRemove"
      @cancelled="closeRemoveAdminModal"
      @succeeded="onAdminRemoved"
    />

    <GPSDeviceAddAdminModal
      v-if="showAddAdminModal"
      :gpsDeviceStorePath="gpsDeviceStorePath"
      :gps="gps"
      @cancelled="closeAddAdminModal"
      @succeeded="closeAddAdminModal"
    />

    <GPSDeleteDeviceModal
      v-if="showDeleteDeviceModal"
      :gpsDeviceStorePath="gpsDeviceStorePath"
      :gps="gps"
      @cancelled="closeDeleteDeviceModal"
      @succeeded="onDeviceDeleted"
    />
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import GPSCoverageMap from './GPSCoverageMap'
import GPSDeviceAdminsTab from './GPSDeviceAdminsTab'
import GPSDeviceRemoveAdminModal from './GPSDeviceRemoveAdminModal'
import GPSDeviceAddAdminModal from './GPSDeviceAddAdminModal'
import GPSDeleteDeviceModal from './GPSDeleteDeviceModal'

import {
  IS_LOADING_GPS,
  GET_GPS,
  GET_GPS_LOAD_ERROR,
  LOAD_ACTIVE_DOG_PROFILE,
  IS_LOADING_ACTIVE_DOG_PROFILE,
  GET_ACTIVE_DOG_PROFILE,
  GET_ACTIVE_DOG_PROFILE_LOAD_ERROR,
  LOAD_SUBSCRIPTION,
  IS_LOADING_SUBSCRIPTION,
  GET_SUBSCRIPTION,
  GET_SUBSCRIPTION_LOAD_ERROR,
  RECALCULATE_SUBSCRIPTION
} from './gpsDeviceStore.js'

import { LOAD_USERS_WITH_IDS, GET_USER_WITH_ID } from '@/modules/users/usersCacheStore.js'

export default {
  name: 'GPS',

  components: {
    Spinner,
    GPSCoverageMap,
    GPSDeviceAdminsTab,
    GPSDeviceRemoveAdminModal,
    GPSDeviceAddAdminModal,
    GPSDeleteDeviceModal
  },

  props: {
    gpsDeviceStorePath: {
      type: String,
      required: true
    },

    weHuntGPSStorePath: {
      type: String,
      required: true
    },

    usersCacheStorePath: {
      type: String,
      required: true
    },

    weHuntGPSRouteName: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      topCardContentVisible: true,
      adminUserToRemove: null,
      showAddAdminModal: false,
      showDeleteDeviceModal: false,

      bottomTabs: [
        {
          index: 0,
          name: 'Admins',
          component: GPSDeviceAdminsTab
        }
        // {
        //   index: 1,
        //   name: 'Map',
        //   component: GPSCoverageMap
        // }
      ],
      selectedBottomTabIndex: null
    }
  },

  mounted() {
    this.loadOwnerUsers()
    this.loadActiveDogProfile()
    this.loadSubscription()
  },

  methods: {
    selectBottomTab(tab) {
      this.selectedBottomTabIndex = tab.index
    },

    loadOwnerUsers() {
      if (!this.gps) {
        return
      }

      const loadUserWithIdsAction = `${this.usersCacheStorePath}/${LOAD_USERS_WITH_IDS}`
      const userIds = [this.gps.ownerUserId, this.gps.gpsOwnerUserId]
      this.$store.dispatch(loadUserWithIdsAction, { userIds })
    },

    loadActiveDogProfile() {
      if (!this.gps) {
        return
      }

      const loadActiveDogProfileAction = `${this.gpsDeviceStorePath}/${LOAD_ACTIVE_DOG_PROFILE}`
      this.$store.dispatch(loadActiveDogProfileAction)
    },

    loadSubscription() {
      if (!this.gps) {
        return
      }

      const loadSubscriptionAction = `${this.gpsDeviceStorePath}/${LOAD_SUBSCRIPTION}`
      this.$store.dispatch(loadSubscriptionAction)
    },

    toggleTopCardContentClicked() {
      this.topCardContentVisible = !this.topCardContentVisible
    },

    weHuntGPSIMEISelected(imei) {
      this.$router.push({
        name: this.weHuntGPSRouteName,
        params: { imei },
        query: this.$route.query
      })
    },

    addAdmin() {
      this.showAddAdminModal = true
    },

    closeAddAdminModal() {
      this.showAddAdminModal = false
    },

    removeAdmin(adminUser) {
      this.adminUserToRemove = adminUser
    },

    onAdminRemoved() {
      this.adminUserToRemove = null

      if (this.gps.adminUserIds.length === 0) {
        this.onDeviceDeleted()
      }
    },

    closeRemoveAdminModal() {
      this.adminUserToRemove = null
    },

    deleteDevice() {
      this.showDeleteDeviceModal = true
    },

    onDeviceDeleted() {
      this.showDeleteDeviceModal = false

      if (this.$router.currentRoute.path.startsWith('/search/gps')) {
        this.$router.push({ name: 'gpsDevicesSearch', query: this.$route.query })
      }

      this.$emit('gps-device-deleted', this.gps)
    },

    closeDeleteDeviceModal() {
      this.showDeleteDeviceModal = false
    },

    recalculateSubscription() {
      if (!this.gps) {
        return
      }

      const recalculateAction = `${this.gpsDeviceStorePath}/${RECALCULATE_SUBSCRIPTION}`
      this.$store.dispatch(recalculateAction)
    }
  },

  computed: {
    isLoading() {
      const isLoadingGPSPath = `${this.gpsDeviceStorePath}/${IS_LOADING_GPS}`
      return this.$store.getters[isLoadingGPSPath]
    },

    gps() {
      const getGPSPath = `${this.gpsDeviceStorePath}/${GET_GPS}`
      return this.$store.getters[getGPSPath]
    },

    canDelete() {
      if (!this.gps) {
        return false
      }

      if (!this.gps.ownerUserId) {
        return false
      }

      return this.gps.ownerUserId !== '[none]'
    },

    gpsOwner() {
      if (!this.gps) {
        return null
      }

      if (!this.gps.gpsOwnerUserId) {
        return null
      }

      const getUserWithIdPath = `${this.usersCacheStorePath}/${GET_USER_WITH_ID}`
      return this.$store.getters[getUserWithIdPath](this.gps.gpsOwnerUserId)
    },

    owner() {
      if (!this.gps) {
        return null
      }

      if (!this.gps.ownerUserId) {
        return null
      }

      const getUserWithIdPath = `${this.usersCacheStorePath}/${GET_USER_WITH_ID}`
      return this.$store.getters[getUserWithIdPath](this.gps.ownerUserId)
    },

    gpsLoadError() {
      const getGPSLoadErrorPath = `${this.gpsDeviceStorePath}/${GET_GPS_LOAD_ERROR}`
      return this.$store.getters[getGPSLoadErrorPath]
    },

    isLoadingActiveDogProfile() {
      const isLoadingActiveDogProfilePath = `${this.gpsDeviceStorePath}/${IS_LOADING_ACTIVE_DOG_PROFILE}`
      return this.$store.getters[isLoadingActiveDogProfilePath]
    },

    activeDogProfile() {
      const getActiveDogProfilePath = `${this.gpsDeviceStorePath}/${GET_ACTIVE_DOG_PROFILE}`
      return this.$store.getters[getActiveDogProfilePath]
    },

    activeDogProfileLoadError() {
      const getActiveDogProfileLoadErrorPath = `${this.gpsDeviceStorePath}/${GET_ACTIVE_DOG_PROFILE_LOAD_ERROR}`
      return this.$store.getters[getActiveDogProfileLoadErrorPath]
    },

    isLoadingSubscription() {
      const isLoadingSubscriptionPath = `${this.gpsDeviceStorePath}/${IS_LOADING_SUBSCRIPTION}`
      return this.$store.getters[isLoadingSubscriptionPath]
    },

    subscription() {
      const getSubscriptionPath = `${this.gpsDeviceStorePath}/${GET_SUBSCRIPTION}`
      return this.$store.getters[getSubscriptionPath]
    },

    subscriptionLoadError() {
      const getSubscriptionLoadErrorPath = `${this.gpsDeviceStorePath}/${GET_SUBSCRIPTION_LOAD_ERROR}`
      return this.$store.getters[getSubscriptionLoadErrorPath]
    },

    adminUserIds() {
      if (!this.gps || !this.gps.adminUserIds) {
        return []
      }

      // Removed owner from admin list
      return this.gps.adminUserIds.filter(adminUserId => adminUserId !== this.gps.ownerUserId)
    },

    selectedBottomTabComponent() {
      if (this.selectedBottomTabIndex === null) {
        return null
      }

      return this.bottomTabs[this.selectedBottomTabIndex].component
    }
  },

  watch: {
    gps() {
      this.adminUserToRemove = null

      this.loadOwnerUsers()
      this.loadActiveDogProfile()
      this.loadSubscription()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.main {
  overflow: auto;
  padding: 10px;
}

.warranty-tag {
  margin-left: 0.5rem;
}

.gps-main {
  padding: 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  .map-container {
    height: 100%;
    width: 100%;
    padding-top: 10px;
  }
}

.card-content {
  .table {
    th {
      padding-right: 0 !important;
      border-bottom: 0;

      span {
        font-weight: normal;
      }
    }

    td {
      border-bottom: 0;

      span {
        font-weight: bold;
        white-space: nowrap;
      }

      .spinner {
        margin-top: 5px;
      }

      a {
        margin-left: 5px;
      }
    }
  }
}

.media-content {
  .button {
    height: 27px;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.card-footer {
  min-height: 48px;

  .loader {
    margin: auto;
  }
}

.bottom-tabs.tabs {
  margin-top: $column-gap * 2;

  a {
    padding: 4px 16px;
  }
}

.tabs-content {
  flex-grow: 1;
  overflow: auto;
}

.table-container {
  width: 100%;
}

.left-column {
  margin-right: $column-gap * 2;
}

.delete-device {
  margin-top: $column-gap * 2;
}
</style>
